import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Ban } from 'lucide-react'
import { ColorDrop } from '@/src/renderer/components/icons/ColorDrop'
import {
  TITLE_FOREGROUND,
  TITLE_WHITE,
  TITLE_PICKER_COLORS,
  TITLE_MAX_OFFSET,
  TITLE_STEPS,
  TITLE_BASE_INDEX,
  parseTitleColorKey,
  titleColorKey,
} from '@/src/renderer/lib/projectIcon/titlePalette'

export interface TitleColorPickerProps {
  /** Der Schlüssel an der Markierung (`blue`, `blue+1`, …); `null` = Standard. */
  value: string | null
  /**
   * Gewählt. `done`: Die Wahl ist fertig (Standard oder eine Abstufung) — der
   * Wähler darf zugehen. Eine Hauptfarbe lässt ihn offen, damit der zweite
   * Klick den Fächer öffnen kann.
   */
  onValueChange: (key: string | null, done: boolean) => void
  /** Überfahren: ein Schlüssel (`null` = Standard) zur Vorschau, `undefined` = Zeiger ist weg. */
  onPreview?: (key: string | null | undefined) => void
}

const OFFSETS = Array.from({ length: TITLE_MAX_OFFSET * 2 + 1 }, (_, i) => i - TITLE_MAX_OFFSET)

/**
 * Der **Farbwähler des Titelwerkzeugs** — dunkel wie das Werkzeug („Die Farbe
 * endet an der Schwelle“: Farben zeigt man auf Dunkel). Vorn Standard (der Ton
 * des Lebensbereichs), dann die 16 Titel-Farben als Tropfen.
 *
 * **Ein Klick auf eine Farbe** wählt sie und öffnet darunter den
 * Varianten-Fächer: sieben Abstufungen um die Standardstufe (Titelfarben-
 * Labor, 2026-09-25). Ein weiterer Klick auf die gewählte Farbe klappt ihn
 * auf oder zu.
 */
export const TitleColorPicker: React.FC<TitleColorPickerProps> = ({ value, onValueChange, onPreview }) => {
  const { t } = useTranslation()
  /*
    **Die gewählte Farbe führt der Wähler selbst** (gemeldet 2026-09-26): `value`
    folgt der Vorschau — schon das Überfahren von Rot machte Rot zur
    „gewählten“ Farbe, und der Klick darauf klappte nur den Fächer, statt Rot
    zu setzen. Beim Öffnen steht noch keine Vorschau; danach zählt nur, was
    angeklickt wurde.
  */
  const [chosen, setChosen] = useState<string | null>(() => value)
  // Die Karte unter dem Zeiger wird die größte, die Nachbarn fallen ab — wie
  // im Titelfarben-Labor. Ohne Zeiger steht die gewählte Stufe in der Mitte.
  const [hoveredOffset, setHoveredOffset] = useState<number | null>(null)
  const current = parseTitleColorKey(chosen)
  const pick = (key: string | null, done: boolean) => {
    setChosen(key)
    onValueChange(key, done)
  }
  // Ist schon eine Farbe gesetzt, stehen ihre Stufen gleich beim Öffnen da
  // (gewünscht 2026-09-26); bei Standard gibt es keine.
  const [fanFamily, setFanFamily] = useState<string | null>(() => parseTitleColorKey(value)?.family ?? null)
  // Der Fächer hängt an der gewählten Familie, NICHT am Wert unter dem
  // Zeiger: Der folgt der Vorschau, und schon das Streifen eines Nachbar-
  // Tropfens auf dem Weg nach unten ließ den Fächer verschwinden (gemeldet
  // 2026-09-26).
  const fan = fanFamily
    ? { family: fanFamily, offset: current?.family === fanFamily ? current.offset : 0 }
    : null

  return (
    <div
      className="canvas-options-bar__flyout canvas-options-bar__flyout--dark canvas-options-bar__flyout--start title-color-picker"
      role="group"
      aria-label={t('projectTitle.color')}
      // Verlässt der Zeiger den Wähler, gilt wieder die gewählte Farbe — auch
      // wenn er zuletzt auf einer Stufe stand (gemeldet 2026-09-26).
      onMouseLeave={() => onPreview?.(undefined)}
    >
      {/* Dieselbe Reihe wie der Farbwähler eines Pinnwand-Elements (`CanvasColorPicker`): Tropfen, die gewählte mit Ring. */}
      <div className="canvas-options-bar__swatches" onMouseLeave={() => onPreview?.(undefined)}>
        <div className={`canvas-options-bar__swatch-pair${chosen === null ? ' is-active' : ''}`}>
          {/* Vorn „keine Farbe“ — dasselbe Zeichen wie „kein Marker“ im Marker-Wähler (gewünscht 2026-09-26). */}
          <button
            type="button"
            className={`canvas-options-bar__swatch canvas-options-bar__swatch--none${chosen === null ? ' is-active' : ''}`}
            aria-label={t('projectTitle.colorNone')}
            aria-pressed={chosen === null}
            onMouseDown={e => e.preventDefault()}
            onMouseEnter={() => onPreview?.(null)}
            onClick={() => { setFanFamily(null); pick(null, true) }}
          >
            <Ban size={18} strokeWidth={2} />
          </button>
        </div>
        <span className="canvas-options-bar__divider canvas-options-bar__divider--flyout" aria-hidden="true" />
        {TITLE_PICKER_COLORS.flatMap(key => (key === TITLE_FOREGROUND ? [key, TITLE_WHITE] : [key])).map(key => {
          // Schwarz und Weiß: ohne Abstufungen — ein Klick setzt es und schließt (gewünscht 2026-09-26).
          if (key === TITLE_FOREGROUND || key === TITLE_WHITE) {
            const on = chosen === key
            return (
              <div key={key} className={`canvas-options-bar__swatch-pair${on ? ' is-active' : ''}`}>
                <button
                  type="button"
                  className={`canvas-options-bar__swatch title-color-picker__swatch${on ? ' is-active' : ''}`}
                  data-color={key}
                  aria-label={t(`titleColors.${key}`)}
                  aria-pressed={on}
                  onMouseDown={e => e.preventDefault()}
                  onMouseEnter={() => onPreview?.(key)}
                  onClick={() => { setFanFamily(null); pick(key, true) }}
                >
                  <ColorDrop fill="currentColor" size={18} outline={on ? 'var(--bar-chip-border)' : undefined} />
                </button>
              </div>
            )
          }
          const selected = current?.family === key
          const shown = selected ? chosen! : key
          return (
            <div key={key} className={`canvas-options-bar__swatch-pair${selected ? ' is-active' : ''}`}>
              <button
                type="button"
                className={`canvas-options-bar__swatch title-color-picker__swatch${selected ? ' is-active' : ''}`}
                // Nur zur Anzeige die freundlichere Stufe 400 der Familie (gewünscht
                // 2026-09-26) — gesetzt wird die Standardstufe, der Fächer zeigt die echten.
                data-family={key}
                aria-label={t(`titleColors.${key}`)}
                aria-pressed={selected}
                aria-expanded={selected ? fanFamily === key : undefined}
                onMouseDown={e => e.preventDefault()}
                onMouseEnter={() => onPreview?.(shown)}
                onClick={() => {
                  if (selected) {
                    setFanFamily(fanFamily === key ? null : key)
                    return
                  }
                  // Die Abstufungen gleich mit öffnen (gewünscht 2026-09-26):
                  // Wer eine Farbe wählt, will oft gleich heller oder dunkler.
                  setFanFamily(key)
                  pick(titleColorKey(key), false)
                }}
              >
                <ColorDrop fill="currentColor" size={18} outline={selected ? 'var(--bar-chip-border)' : undefined} />
              </button>
            </div>
          )
        })}
      </div>
      {fan && (
        <div
          className="title-color-picker__fan"
          role="radiogroup"
          aria-label={t('projectTitle.variants')}
          onMouseLeave={() => setHoveredOffset(null)}
        >
          {OFFSETS.map(offset => {
            const on = fan.offset === offset
            const step = TITLE_STEPS[TITLE_BASE_INDEX.light + offset]
            return (
              <button
                key={offset}
                type="button"
                role="radio"
                aria-checked={on}
                aria-label={`${t(`titleColors.${fan.family}`)} ${step}`}
                className={`title-color-picker__shade${on ? ' title-color-picker__shade--current' : ''}`}
                data-distance={Math.abs(offset - (hoveredOffset ?? fan.offset))}
                data-color={titleColorKey(fan.family, offset)}
                onMouseDown={e => e.preventDefault()}
                onMouseEnter={() => {
                  setHoveredOffset(offset)
                  onPreview?.(titleColorKey(fan.family, offset))
                }}
                onMouseLeave={() => onPreview?.(undefined)}
                onClick={() => pick(titleColorKey(fan.family, offset), true)}
              >
                <span className="title-color-picker__shade-step">{step}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
